import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { ENTITY_NAME_LINK_STYLE, TABLE_SORTER_TOOLTIP } from '../../../shared/ui/list-styles.js';
import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Button, Select, Space, Table, Tooltip } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import type { ColumnType } from 'antd/es/table/interface';

import { useOrderedColumns } from '../../../shared/grid/useOrderedColumns.js';
import type { GridProfile } from '../../../shared/grid/useGridProfile.js';
import { formatPersonName } from '../../../shared/format/index.js';
import { EntityHistoryButton } from '../../activity/components/EntityHistoryButton.js';
import { DeleteEntityButton } from '../../../shared/ui/DeleteEntityButton.js';
import { EntityTaskDueChip } from '../../../components/entity-detail/EntityTaskDueChip.js';
import { LinkCell, linkSortLabel, type OwnLink } from '../../../shared/ui/LinkCell.js';
import {
  FLEX_TITLE_CELL_CLASS,
  tableMinWidth as computeTableMinWidth,
} from '../../../shared/ui/table-width.js';
import type { TaskItem, TaskStatus } from '../api/tasks.client.js';
import type { TaskColumnKey } from '../hooks/useTaskGridProfile.js';
import {
  formatDateTime,
  PRIORITY_TONE,
  getTaskPriorityLabel,
  getTaskStatusOptions,
  PRIORITY_ORDER,
  STATUS_ORDER,
} from '../lib/taskMeta.js';

// Собственные привязки задачи, приведённые к общей форме ячейки. Наследование
// («компания сделки, если у задачи своей нет») считает сервер — см. LinkCell.
function ownCompany(task: TaskItem): OwnLink | null {
  return task.company ? { id: task.company.id, label: task.company.name } : null;
}

function ownDeal(task: TaskItem): OwnLink | null {
  return task.deal ? { id: task.deal.id, label: task.deal.title } : null;
}

function ownContact(task: TaskItem): OwnLink | null {
  return task.contact
    ? {
        id: task.contact.id,
        label: formatPersonName(task.contact.firstName, task.contact.lastName),
      }
    : null;
}

interface TasksGridProps {
  rows: TaskItem[];
  /** Вид грида: порядок, скрытые колонки и перестановка — общий хук. */
  profile: GridProfile;
  onEdit: (task: TaskItem) => void;
  onDelete: (task: TaskItem) => void;
  onQuickStatus: (task: TaskItem, status: TaskStatus) => void;
  quickStatusPendingId?: string | null;
  deletePendingId?: string | null;
  /** Колонки, не имеющие смысла в контексте (например «Сделка» на карточке сделки). */
  suppressColumns?: TaskColumnKey[];
  compact?: boolean;
  /** Выбор строк — включается там, где он осмыслен (страница «Задачи»). */
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
}

export function TasksGrid({
  rows,
  profile,
  onEdit,
  onDelete,
  onQuickStatus,
  quickStatusPendingId,
  deletePendingId,
  suppressColumns = [],
  compact = false,
  selectedIds,
  onSelectionChange,
}: TasksGridProps): ReactElement {
  useI18nSubscription();
  const locale = i18n.locale;
  const columnMap: Record<TaskColumnKey, ColumnType<TaskItem>> = {
    title: {
      title: i18n.t('crm.features.features.tasks.components.tasksgrid.название'),
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => a.title.localeCompare(b.title, locale),
      render: (_: unknown, task) => (
        // Название ведёт на карточку; редактирование осталось на иконке действия.
        <Link
          to={`/tasks/${task.id}`}
          className={FLEX_TITLE_CELL_CLASS}
          style={ENTITY_NAME_LINK_STYLE}
          title={task.title}
          onClick={(event) => event.stopPropagation()}
        >
          {task.title}
        </Link>
      ),
    },
    status: {
      title: i18n.t('crm.features.features.tasks.components.tasksgrid.статус'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      sorter: (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status],
      render: (_: unknown, task) => (
        <Select
          size="small"
          variant="borderless"
          aria-label={i18n.t('crm.features.task_detail.row_status', { title: task.title })}
          value={task.status}
          options={getTaskStatusOptions()}
          loading={quickStatusPendingId === task.id}
          style={{ width: '100%' }}
          onChange={(value) => onQuickStatus(task, value as TaskStatus)}
        />
      ),
    },
    priority: {
      title: i18n.t('crm.features.features.tasks.components.tasksgrid.приоритет'),
      dataIndex: 'priority',
      key: 'priority',
      width: 105,
      sorter: (a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority],
      render: (_: unknown, task) => (
        <EntityStatusChip tone={PRIORITY_TONE[task.priority]}>
          {getTaskPriorityLabel(task.priority)}
        </EntityStatusChip>
      ),
    },
    dueAt: {
      title: i18n.t('crm.features.features.tasks.components.tasksgrid.срок'),
      dataIndex: 'dueAt',
      key: 'dueAt',
      width: 170,
      sorter: (a, b) =>
        (a.dueAt ? new Date(a.dueAt).getTime() : Infinity) -
        (b.dueAt ? new Date(b.dueAt).getTime() : Infinity),
      render: (_: unknown, task) => (
        <EntityTaskDueChip
          dueAt={task.dueAt ?? null}
          closed={task.status === 'done' || task.status === 'cancelled'}
        />
      ),
    },
    assignee: {
      title: i18n.t('crm.features.features.tasks.components.tasksgrid.исполнитель'),
      key: 'assignee',
      width: 120,
      sorter: (a, b) =>
        (a.assigneeMember?.user.name ?? '').localeCompare(
          b.assigneeMember?.user.name ?? '',
          locale,
        ),
      render: (_: unknown, task) => (
        <span style={{ color: 'var(--ant-color-text-secondary)' }}>
          {task.assigneeMember?.user.name ?? '—'}
        </span>
      ),
    },
    // Три колонки привязок показывают ЭФФЕКТИВНУЮ привязку: задача относится к
    // фирме, человеку или сделке и тогда, когда связь стоит у её соседа, и фильтр
    // на сервере находит её именно так. Пустая ячейка у найденной строки читалась
    // бы как расхождение грида с фильтром. Наследование считает сервер и
    // присылает в `inheritedLinks`; сортировка идёт по показанному значению.
    deal: {
      title: i18n.t('crm.features.features.tasks.components.tasksgrid.сделка'),
      key: 'deal',
      width: 115,
      sorter: (a, b) =>
        (linkSortLabel(ownDeal(a), a.inheritedLinks?.deal) ?? '').localeCompare(
          linkSortLabel(ownDeal(b), b.inheritedLinks?.deal) ?? '',
          locale,
        ),
      render: (_: unknown, task) => (
        <LinkCell kind="deal" own={ownDeal(task)} inherited={task.inheritedLinks?.deal} />
      ),
    },
    company: {
      title: i18n.t('crm.features.features.tasks.components.tasksgrid.компания'),
      key: 'company',
      width: 105,
      sorter: (a, b) =>
        (linkSortLabel(ownCompany(a), a.inheritedLinks?.company) ?? '').localeCompare(
          linkSortLabel(ownCompany(b), b.inheritedLinks?.company) ?? '',
          locale,
        ),
      render: (_: unknown, task) => (
        <LinkCell kind="company" own={ownCompany(task)} inherited={task.inheritedLinks?.company} />
      ),
    },
    contact: {
      title: i18n.t('crm.features.features.tasks.components.tasksgrid.контакт'),
      key: 'contact',
      width: 105,
      sorter: (a, b) =>
        (linkSortLabel(ownContact(a), a.inheritedLinks?.contact) ?? '').localeCompare(
          linkSortLabel(ownContact(b), b.inheritedLinks?.contact) ?? '',
          locale,
        ),
      render: (_: unknown, task) => (
        <LinkCell kind="contact" own={ownContact(task)} inherited={task.inheritedLinks?.contact} />
      ),
    },
    createdAt: {
      title: i18n.t('crm.features.features.tasks.components.tasksgrid.дата_создания'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (_: unknown, task) => (
        <span style={{ color: 'var(--ant-color-text-tertiary)' }}>
          {formatDateTime(task.createdAt)}
        </span>
      ),
    },
  };

  const actionsColumn: ColumnType<TaskItem> = {
    title: i18n.t('crm.features.features.tasks.components.tasksgrid.действия'),
    key: 'actions',
    width: 105,
    render: (_: unknown, task) => (
      <Space size="small">
        <Tooltip title={i18n.t('crm.features.features.tasks.components.tasksgrid.редактировать')}>
          <Button
            type="text"
            size="small"
            onClick={() => onEdit(task)}
            aria-label={i18n.t('crm.features.features.tasks.components.tasksgrid.редактировать_2')}
          >
            <EditOutlined />
          </Button>
        </Tooltip>
        <EntityHistoryButton
          entity="task"
          entityId={task.id}
          title={i18n.t('crm.features.features.tasks.components.tasksgrid.история_задачи')}
          subject={task.title}
          iconOnly
        />
        <DeleteEntityButton
          iconOnly
          label={i18n.t('crm.features.features.tasks.components.tasksgrid.удалить_задачу')}
          title={i18n.t('crm.features.features.tasks.components.tasksgrid.удалить_задачу_2')}
          description={i18n.t(
            'crm.features.features.tasks.components.tasksgrid.задачу_можно_будет_восстановить_только_из_',
          )}
          loading={deletePendingId === task.id}
          onConfirm={() => onDelete(task)}
        />
      </Space>
    ),
  };

  const ordered = useOrderedColumns<TaskItem>({
    profile,
    columnMap,
    suppress: suppressColumns,
    trailing: [actionsColumn],
  });

  // Минимальная ширина таблицы: фиксированные колонки + пол под название.
  // Общий контракт списков (shared/ui/table-width.ts): на узком экране таблица
  // прокручивается внутри контейнера, а не сжимает «Название» по одной букве.
  const tableMinWidth = computeTableMinWidth(ordered.columns, {
    selection: onSelectionChange !== undefined,
  });

  return (
    <Table<TaskItem>
      className="tasks-grid"
      tableLayout="fixed"
      showSorterTooltip={TABLE_SORTER_TOOLTIP}
      size="small"
      rowKey="id"
      dataSource={rows}
      columns={ordered.columns}
      components={ordered.components}
      {...(onSelectionChange
        ? {
            rowSelection: {
              columnWidth: 48,
              getCheckboxProps: (task: TaskItem) => ({
                'aria-label': task.title,
                title: task.title,
              }),
              selectedRowKeys: selectedIds ?? [],
              // Грид рендерится по одному на группу: без preserve antd отдавал бы
              // только ключи своей таблицы и стирал выбор в остальных группах.
              preserveSelectedRowKeys: true,
              onChange: (keys: React.Key[]) => onSelectionChange(keys.map(String)),
            },
          }
        : {})}
      // Локальная пагинация, а не URL-синхронизированная (`EntityTable`): грид
      // рендерится по несколько штук на экране — при группировке и в панелях
      // карточек, — и общие URL-параметры страницы они бы делили друг с другом.
      pagination={
        rows.length > (compact ? 10 : 20) ? { pageSize: compact ? 10 : 20, size: 'small' } : false
      }
      scroll={{ x: compact ? 'max-content' : tableMinWidth }}
    />
  );
}
